import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, LayoutDashboard, ArrowRight, TrendingUp, Rocket } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine,
} from "recharts";
import { toast } from "sonner";
import BrandHeader from "@/components/BrandHeader";
import Footer from "@/components/Footer";
import { fetchMyAssessments } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso || "";
  }
};

export default function Dashboard() {
  const { isAuthed, ready, auth } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!ready) return;
    if (!isAuthed) {
      navigate("/login?next=/mein-verlauf");
      return;
    }
    fetchMyAssessments()
      .then(setData)
      .catch(() => toast.error("Verlauf konnte nicht geladen werden."))
      .finally(() => setLoading(false));
  }, [ready, isAuthed, navigate]);

  const trend = useMemo(() => {
    if (!data?.assessments?.length) return [];
    return [...data.assessments]
      .reverse()
      .map((a) => ({ date: fmtDate(a.created_at), note: a.overall_note }));
  }, [data]);

  if (!ready || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-brand-orange" />
      </div>
    );
  }

  const items = data?.assessments || [];

  return (
    <div className="min-h-screen relative z-10" data-testid="dashboard-page">
      <BrandHeader minimal />
      <div className="max-w-5xl mx-auto px-6 sm:px-10 py-12">
        <div className="flex items-center gap-2 mb-1">
          <LayoutDashboard className="h-5 w-5 text-brand-orange" />
          <h1 className="font-display uppercase text-3xl tracking-tight text-white">Mein Verlauf</h1>
        </div>
        <p className="text-sm text-white/50">
          {auth?.name ? `${auth.name} · ` : ""}{items.length} gespeicherte{items.length === 1 ? "r" : ""} Check{items.length === 1 ? "" : "s"}
        </p>

        {items.length === 0 ? (
          <div className="mt-12 border border-white/10 bg-card p-10 text-center">
            <Rocket className="h-10 w-10 text-brand-orange mx-auto" />
            <p className="mt-5 text-white/70">Noch kein Check gespeichert.</p>
            <p className="mt-2 text-sm text-white/40">Führen Sie Ihren ersten Tragfähigkeits-Check durch – als eingeloggter Nutzer wird er automatisch hier gespeichert.</p>
            <Link to="/" data-testid="dashboard-start-check"
              className="mt-6 inline-flex items-center gap-2 h-12 px-7 bg-brand-orange text-black hover:bg-white font-bold uppercase tracking-wider text-sm transition-colors">
              Check starten <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        ) : (
          <>
            {trend.length > 1 && (
              <div className="mt-10 border border-white/10 bg-card p-6" data-testid="dashboard-trend">
                <div className="flex items-center gap-2 mb-5">
                  <TrendingUp className="h-4 w-4 text-brand-orange" />
                  <span className="font-display text-sm uppercase tracking-wide text-white">Entwicklung Ihrer Gesamtnote</span>
                  <span className="text-xs text-white/40">(1 = Spitze, 6 = kritisch)</span>
                </div>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trend} margin={{ top: 10, right: 20, left: -10, bottom: 0 }}>
                      <CartesianGrid stroke="#ffffff10" vertical={false} />
                      <XAxis dataKey="date" stroke="#ffffff40" fontSize={11} tickLine={false} />
                      <YAxis domain={[1, 6]} reversed stroke="#ffffff40" fontSize={11} tickLine={false} ticks={[1, 2, 3, 4, 5, 6]} />
                      <ReferenceLine y={2} stroke="#22c55e" strokeDasharray="4 4" />
                      <Tooltip
                        contentStyle={{ background: "#111", border: "1px solid #ffffff20", borderRadius: 0, color: "#fff" }}
                        labelStyle={{ color: "#f18e00" }}
                      />
                      <Line type="monotone" dataKey="note" stroke="#f18e00" strokeWidth={2.5} dot={{ r: 4, fill: "#f18e00" }} activeDot={{ r: 6 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            <div className="mt-8 space-y-3">
              {items.map((a, i) => (
                <motion.div
                  key={a.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: i * 0.04 }}
                >
                  <Link
                    to={`/ergebnis/${a.id}`}
                    data-testid={`dashboard-check-${a.id}`}
                    className="flex items-center gap-5 border border-white/10 bg-card p-5 hover:border-brand-orange/50 transition-colors group"
                  >
                    <div
                      className="h-14 w-14 flex flex-col items-center justify-center shrink-0 font-display"
                      style={{ backgroundColor: a.overall_zone_color, color: "#000" }}
                    >
                      <span className="text-xl leading-none">{Number(a.overall_note).toFixed(1).replace(".", ",")}</span>
                      <span className="text-[0.55rem] uppercase tracking-wider">Note</span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-display text-white uppercase tracking-tight">{a.overall_zone_name}</p>
                      <p className="text-xs text-white/50 mt-0.5">
                        {fmtDate(a.created_at)}{a.branche_label ? ` · ${a.branche_label}` : ""}
                      </p>
                    </div>
                    <ArrowRight className="h-5 w-5 text-white/30 group-hover:text-brand-orange group-hover:translate-x-1 transition-all" />
                  </Link>
                </motion.div>
              ))}
            </div>

            <Link to="/" data-testid="dashboard-new-check"
              className="mt-8 inline-flex items-center gap-2 h-12 px-7 border border-brand-orange text-brand-orange hover:bg-brand-orange/10 font-bold uppercase tracking-wider text-sm transition-colors">
              Neuen Check starten <ArrowRight className="h-4 w-4" />
            </Link>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}
